



'use client';
import { useState, useEffect, useRef, useMemo, useCallback, memo } from 'react';
import '../Trainer/TrainerDashboard.css';
import '../common/lms-pages.css';
import './BusinessPulse.css';
import BusinessPulse from './BusinessPulse.jsx';
import BatchAttendanceReport from './BatchAttendanceReport.jsx';
import TrainerFeedbackReport from './TrainerFeedbackReport.jsx';
import AdminOverviewSkeleton from './AdminOverviewSkeleton.jsx';
import AccessRequestsAdmin from '../AccessCenter/AccessRequestsAdmin.jsx';
import { isComingSoon } from '../../../utils/comingSoon';
import { onPageRefresh } from '../../layouts/pageRefresh';
import { silenceFor } from '../common/Loader/busy';
import OnlineStudentsBox from '../common/OnlineStudents/OnlineStudentsBox.jsx';
import OnlineTrainersBox from '../common/OnlineStudents/OnlineTrainersBox.jsx';
import {
  Users,
  BookOpen,
  GraduationCap,
  TrendingUp,
  Search,
  Eye,
  CheckCircle,
  Clock,
  UserCheck,
  Building,
  MapPin,
  Calendar,
  User,
  Award,
  Video,
  ExternalLink,
  X,
  Mail,
  Phone,
  Edit,
  Tag,
  Star,
} from 'lucide-react';
import { motion } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
// react-toastify removed; the app has one <ToastHost/> in App.js
import { toast } from '../common/Toast/toast';
import { getJobById } from '../../../services/JobsService';
import NewCustomDataTable from '../common/NewCustomDataTable.jsx';
import AsyncSelectDropdown from '../common/NewCustomAsyncSelectDropdown.jsx';
import StudentDetailModal from '../Trainer/StudentDetailModal.jsx';
import { useAuth } from '../../../context/AuthContext';
import { safeHtml } from '../../../utils/safeHtml';
import { useContext } from 'react';
import { ThemeContext } from '../../../context/ThemeContext';
import { useWaypoint } from '../common/waypoint/WaypointProvider.jsx';
import '../common/waypoint/waypoint.css';

import {
  getDashboardOverview,
  getDashboardMetrics,
  getDashboardTabData,
  getDashboardTabDetail,
  getDashboardStudents,
  getDashboardStudentView,
  getBatchedStudents,
  getFullStudent,
  getStudentAttendance,
  getStudentQuizHistory,
} from '../../../services/DashboardOverviewService.js';

// Define ErrorAlert component HERE
const ErrorAlert = ({ error, section, onRetry }) => {
  if (!error) return null;
  return (
    <div>
      <div>
        <i></i>
        <strong>{error}</strong>
        <button type="button" onClick={onRetry}>
          Retry
        </button>
      </div>
    </div>
  );
};

const __dashboardMemoryCache = {
  hydrated: false,
  dashboardData: null,
  stats: null,
  studentPageCache: {},
  tabsHydrated: {
    batches: false,
    courses: false,
    students: false,
    trainers: false,
    jobs: false,
    events: false,
  },
  ui: {
    activeTab: 'overview',
    currentPages: {
      batches: 1,
      courses: 1,
      students: 1,
      trainers: 1,
      jobs: 1,
      events: 1,
    },
    searchTerm: '',
    selectedBatchFilter: null,
    selectedCourseFilter: null,
    selectedStudentFilter: null,
    selectedTrainerFilter: null,
    selectedJobFilter: null,
    selectedEventFilter: null,
  },
};

/* The topbar's refresh button remounts the page, but this object outlives
   the component -- left alone, the fresh mount would find it hydrated and
   show the same figures without asking the API. Dropped the same way the
   dashboard's own Refresh button drops it. */
onPageRefresh(() => {
  __dashboardMemoryCache.hydrated = false;
  __dashboardMemoryCache.tabsHydrated = {
    batches: false, courses: false, students: false,
    trainers: false, jobs: false, events: false,
  };
  __dashboardMemoryCache.studentPageCache = {};
  __dashboardMemoryCache.dashboardData = null;
  __dashboardMemoryCache.stats = null;
});

// Add this function at the top of AdminDashboard component, after the navigate declaration
const getDashboardTitle = (user) => {
  if (user?.profiles?.some((profile) => profile.label?.toLowerCase().includes('crm'))) {
    return 'CRM Dashboard';
  }
  return 'Admin Dashboard';
};


const AdminDashboard = ({ user }) => {
  // Waypoint, so a name on this dashboard can be opened rather than just read.
  const { open: openEntity } = useWaypoint();
  const navigate = useNavigate();
  const { background, changeBackground, backgroundOption } = useContext(ThemeContext);

  const isCRM = useMemo(() =>
    user?.profiles?.some((profile) => profile.label?.toLowerCase().includes('crm')),
    [user]
  );

  // State management
  const [loading, setLoading] = useState(true);

  // Clear cache when user changes to prevent cross-account data leakage and ensure tabs re-fetch
  useEffect(() => {
    __dashboardMemoryCache.hydrated = false;
    __dashboardMemoryCache.tabsHydrated = {
      batches: false, courses: false, students: false,
      trainers: false, jobs: false, events: false,
    };
    __dashboardMemoryCache.studentPageCache = {};
    __dashboardMemoryCache.dashboardData = null;
    __dashboardMemoryCache.stats = null;
  }, [user?.id]);
  const [tabLoading, setTabLoading] = useState(false); // Separate loading for tab data
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [showBatchModal, setShowBatchModal] = useState(false);
  const [batchModalTab, setBatchModalTab] = useState('details');
  const [courseDetails, setCourseDetails] = useState(null);
  const [trainerDetails, setTrainerDetails] = useState(null);
  const [jobDetails, setJobDetails] = useState(null);
  const [eventDetails, setEventDetails] = useState(null);
  const [eventTab, setEventTab] = useState('details');
  const [loadingMoreTab, setLoadingMoreTab] = useState(false);
  const loadingMoreTabRef = useRef(false);
  const coursesEndRef = useRef(null);
  const studentsTabEndRef = useRef(null);
  const trainersEndRef = useRef(null);
  const jobsEndRef = useRef(null);
  const eventsEndRef = useRef(null);
  const [loadingMoreEventsTab, setLoadingMoreEventsTab] = useState(false);
  const [loadingMoreJobsTab, setLoadingMoreJobsTab] = useState(false);
  const [loadingMoreTrainersTab, setLoadingMoreTrainersTab] = useState(false);
  const [loadingMoreStudentsTab, setLoadingMoreStudentsTab] = useState(false);
  const [selectedStudentData, setSelectedStudentData] = useState(null);
  const [showStudentModal, setShowStudentModal] = useState(false);
  const [showFullJDModal, setShowFullJDModal] = useState(false);
  const [selectedJobForFullJD, setSelectedJobForFullJD] = useState(null);





  // Batch Dropdown State for NewCustomAsyncSelectDropdown
  const [dropdownBatchOptions, setDropdownBatchOptions] = useState([]);
  const [dropdownBatchPage, setDropdownBatchPage] = useState(1);
  const [dropdownBatchSearch, setDropdownBatchSearch] = useState('');
  const [dropdownHasMoreBatches, setDropdownHasMoreBatches] = useState(true);
  const [loadingDropdownBatches, setLoadingDropdownBatches] = useState(false);
  const isFetchingDropdownBatchesRef = useRef(false);

  // Course Dropdown State for Students Tab
  const [dropdownCourseOptions, setDropdownCourseOptions] = useState([]);
  const [dropdownCoursePage, setDropdownCoursePage] = useState(1);
  const [dropdownCourseSearch, setDropdownCourseSearch] = useState('');
  const [dropdownHasMoreCourses, setDropdownHasMoreCourses] = useState(true);
  const [loadingDropdownCourses, setLoadingDropdownCourses] = useState(false);
  const isFetchingDropdownCoursesRef = useRef(false);


  const [itemsPerPage, setItemsPerPage] = useState(25);

  // Data states
  // recent* is the Overview's, from /dashboard/overview; events is the
  // Events tab's list. The tab lists no longer share keys with the overview,
  // so opening a tab cannot replace the Recent panels with its first page.
  const [dashboardData, setDashboardData] = useState({
    recentBatches: [],
    recentEvents: [],
    recentJobs: [],
    events: [],
  });

  const [currentPages, setCurrentPages] = useState({
    batches: 1,
    courses: 1,
    students: 1,
    trainers: 1,
    jobs: 1,
    events: 1,
  });
  const [tabPagination, setTabPagination] = useState({
    batches: { total: 0 },
    courses: { total: 0 },
    students: { total: 0 },
    trainers: { total: 0 },
    jobs: { total: 0 },
    events: { total: 0 },
  });

  const [enhancedBatches, setEnhancedBatches] = useState([]);

  // Filter states for tabs
  const [selectedBatchFilter, setSelectedBatchFilter] = useState(null);
  const [selectedCourseFilter, setSelectedCourseFilter] = useState(null);
  const [selectedStudentFilter, setSelectedStudentFilter] = useState(null);
  const [selectedTrainerFilter, setSelectedTrainerFilter] = useState(null);
  const [selectedJobFilter, setSelectedJobFilter] = useState(null);
  const [selectedEventFilter, setSelectedEventFilter] = useState(null);
  const [batchFilter, setBatchFilter] = useState('active');

  // Filtered data states
  const [filteredCourses, setFilteredCourses] = useState([]);
  const [filteredStudents, setFilteredStudents] = useState([]);
  const [filteredTrainers, setFilteredTrainers] = useState([]);
  const [filteredJobs, setFilteredJobs] = useState([]);

  const [stats, setStats] = useState({
    totalBatches: 0,
    activeBatches: 0,
    completedBatches: 0,
    totalCourses: 0,
    totalStudents: 0,
    totalTrainers: 0,
    totalEvents: 0,
    totalJobs: 0,
  });

  // Add after existing state declarations
  const [apiErrors, setApiErrors] = useState({
    batches: null,
    courses: null,
    students: null,
    trainers: null,
    events: null,
    jobs: null,
  });

  const isMountedRef = useRef(false);
  const isFetchingRef = useRef(false);
  const hasShownToast = useRef(false);
  const { hasPermission } = useAuth();
  useEffect(() => {
    if (!hasPermission('dashboard', 'view', 'lms')) {
      if (!hasShownToast.current) {
        navigate('/');
        // console.log("hello");
        hasShownToast.current = true;
      }
    }
  }, [hasPermission, navigate]);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const loadBatchOptions = useCallback(async ({ search, page, limit }) => {
    try {
      const resp = await getDashboardTabData({
        tab: 'batches',
        page,
        limit,
        search: search || '',
      });
      const payload = resp?.data || {};
      return {
        options: (payload.items || []).map((b) => ({ value: b.id, label: b.name })),
        hasNext: payload.pagination?.hasNext,
      };
    } catch (e) {
      return { options: [], hasNext: false };
    }
  }, []);

  const loadDropdownBatches = useCallback(async (search = '', page = 1, append = false) => {
    if (isFetchingDropdownBatchesRef.current) return;
    try {
      setLoadingDropdownBatches(true);
      isFetchingDropdownBatchesRef.current = true;
      const resp = await getDashboardTabData({
        tab: 'batches',
        page,
        limit: 10,
        search: search || '',
      });
      const payload = resp?.data || {};
      const items = payload.items || [];
      const hasNext = payload.pagination?.hasNext || false;

      const newOptions = items.map((b) => ({ value: b.id, label: b.name }));
      if (append) {
        setDropdownBatchOptions((prev) => [...prev, ...newOptions]);
      } else {
        setDropdownBatchOptions(newOptions);
      }
      setDropdownHasMoreBatches(hasNext);
      setDropdownBatchPage(page);
    } catch (e) {
      console.error('Error loading batches for dropdown:', e);
    } finally {
      setLoadingDropdownBatches(false);
      isFetchingDropdownBatchesRef.current = false;
    }
  }, []);

  const handleDropdownBatchSearch = useCallback((searchTerm) => {
    setDropdownBatchSearch(searchTerm);
    setDropdownBatchPage(1);
    loadDropdownBatches(searchTerm, 1, false);
  }, [loadDropdownBatches]);

  const handleDropdownBatchLoadMore = useCallback(() => {
    if (!dropdownHasMoreBatches || loadingDropdownBatches) return;
    loadDropdownBatches(dropdownBatchSearch, dropdownBatchPage + 1, true);
  }, [dropdownBatchSearch, dropdownBatchPage, dropdownHasMoreBatches, loadingDropdownBatches, loadDropdownBatches]);

  const handleDropdownBatchOpen = useCallback(() => {
    if (dropdownBatchOptions.length === 0 && !loadingDropdownBatches) {
      loadDropdownBatches('', 1, false);
    }
  }, [dropdownBatchOptions.length, loadingDropdownBatches, loadDropdownBatches]);

  const loadDropdownCourses = useCallback(async (search = '', page = 1, append = false) => {
    if (isFetchingDropdownCoursesRef.current) return;
    try {
      setLoadingDropdownCourses(true);
      isFetchingDropdownCoursesRef.current = true;
      const resp = await getDashboardTabData({
        tab: 'courses',
        page,
        limit: 10,
        search: search || '',
      });
      const payload = resp?.data || {};
      const items = payload.items || [];
      const hasNext = payload.pagination?.hasNext || false;

      const newOptions = items.map((c) => ({ value: c.id, label: c.name, course: c }));
      if (append) {
        setDropdownCourseOptions((prev) => [...prev, ...newOptions]);
      } else {
        setDropdownCourseOptions(newOptions);
      }
      setDropdownHasMoreCourses(hasNext);
      setDropdownCoursePage(page);
    } catch (e) {
      console.error('Error loading courses for dropdown:', e);
    } finally {
      setLoadingDropdownCourses(false);
      isFetchingDropdownCoursesRef.current = false;
    }
  }, []);

  const handleDropdownCourseSearch = useCallback((searchTerm) => {
    setDropdownCourseSearch(searchTerm);
    setDropdownCoursePage(1);
    loadDropdownCourses(searchTerm, 1, false);
  }, [loadDropdownCourses]);

  const handleDropdownCourseLoadMore = useCallback(() => {
    if (!dropdownHasMoreCourses || loadingDropdownCourses) return;
    loadDropdownCourses(dropdownCourseSearch, dropdownCoursePage + 1, true);
  }, [dropdownCourseSearch, dropdownCoursePage, dropdownHasMoreCourses, loadingDropdownCourses, loadDropdownCourses]);

  const handleDropdownCourseOpen = useCallback(() => {
    if (dropdownCourseOptions.length === 0 && !loadingDropdownCourses) {
      loadDropdownCourses('', 1, false);
    }
  }, [dropdownCourseOptions.length, loadingDropdownCourses, loadDropdownCourses]);

  /* Every exit from loadTabData runs this. It used to live in a
     useEffect keyed on [tabLoading] -- which is why paging stopped: see
     the note on that effect's replacement below. */
  const endTabLoad = useCallback(() => {
    loadingMoreTabRef.current = false;
    setLoadingMoreTab(false);
    setLoadingMoreStudentsTab(false);
    setLoadingMoreTrainersTab(false);
    setLoadingMoreJobsTab(false);
    setLoadingMoreEventsTab(false);
  }, []);

  const loadTabData = useCallback(
    async (tableKey, page, search = '') => {
      if (tableKey === 'students') {
        const cacheKey = `${page}|${search || ''}`;
        const cached = __dashboardMemoryCache.studentPageCache[cacheKey];
        if (cached) {
          /* Append past page 1, the same way the live path below does.
             This replaced the list outright, so a cached page 2 -- scroll,
             leave the tab, come back, scroll again -- threw away every row
             above it and the list appeared to reset rather than grow. */
          setFilteredStudents((prev) => {
            if (page === 1) return cached.items;
            const seen = new Set(prev.map((r) => r.id));
            return [...prev, ...cached.items.filter((r) => !seen.has(r.id))];
          });
          setTabPagination((prev) => ({ ...prev, students: { total: cached.total } }));
          // A cache hit is still a page finishing, and the scroll that
          // asked for it is still holding the guard.
          endTabLoad();
          return;
        }
      } else if (page === 1 && !search && __dashboardMemoryCache.tabsHydrated[tableKey]) {
        endTabLoad();
        return;
      }

      try {
        // Past page 1 the request came from the scroll: the rows already on
        // screen stay and the batch is added under them, so the table must
        // not be veiled for it. The per-tab loadingMore flags carry the
        // spinner in the foot instead.
        if (page <= 1) setTabLoading(true);

        /* Every row arrives display-ready. Students come with courseNames
           and their batch assignments (GET /dashboard/students); batches
           with studentCount and status, filtered by the Active / Completed
           toggle and the batch picker; events with trainerNames and
           batchNames. The page used to build all of that from receipts and
           raw ids here, and split the batches itself over whatever page of
           20 it held. */
        const response =
          tableKey === 'students'
            ? await getDashboardStudents({ page, limit: itemsPerPage, search })
            : await getDashboardTabData({
                tab: tableKey,
                page,
                limit: itemsPerPage,
                search,
                scope: 'admin',
                ...(tableKey === 'batches'
                  ? {
                      state: batchFilter === 'completed' ? 'completed' : 'active',
                      batchId: selectedBatchFilter?.value || undefined,
                    }
                  : {}),
              });

        const payload = response?.data || {};
        const items = Array.isArray(payload.items) ? payload.items : [];
        const total = Number(payload.pagination?.total) || 0;
        setTabPagination((prev) => ({ ...prev, [tableKey]: { total } }));

        // Guarded by id so a repeated page cannot double up a row.
        const withPage = (prev) => {
          if (page === 1) return items;
          const seen = new Set((prev || []).map((r) => r.id));
          return [...(prev || []), ...items.filter((r) => !seen.has(r.id))];
        };

        if (tableKey === 'batches') setEnhancedBatches(withPage);
        if (tableKey === 'courses') setFilteredCourses(withPage);
        if (tableKey === 'trainers') setFilteredTrainers(withPage);
        if (tableKey === 'jobs') setFilteredJobs(withPage);
        // Events replaced the list with each page, so scrolling showed only
        // the page that landed last.
        if (tableKey === 'events') {
          setDashboardData((prev) => ({ ...prev, events: withPage(prev.events) }));
        }
        if (tableKey === 'students') {
          setFilteredStudents(withPage);
          __dashboardMemoryCache.studentPageCache[`${page}|${search || ''}`] = { items, total };
        }
        __dashboardMemoryCache.tabsHydrated[tableKey] = true;
        setApiErrors((prev) => ({ ...prev, [tableKey]: null }));
      } catch (e) {
        console.error(`Error loading tab data for ${tableKey}:`, e);
        setApiErrors((prev) => ({ ...prev, [tableKey]: e.message || `Failed to load ${tableKey}` }));
      } finally {
        setTabLoading(false);
        endTabLoad();
      }
    },
    [itemsPerPage, batchFilter, selectedBatchFilter, endTabLoad]
  );

  const handleRowsPerPageChange = useCallback(
    (newLimit) => {
      setItemsPerPage(newLimit);
      if (activeTab && activeTab !== 'overview') {
        setCurrentPages((prev) => ({ ...prev, [activeTab]: 1 }));
        __dashboardMemoryCache.tabsHydrated[activeTab] = false;
        // The useEffect watching activeTab/currentPages will trigger loadTabData
        // But since itemsPerPage just changed, we might need a manual trigger or
        // ensure itemsPerPage is in the effect dependencies.
        loadTabData(activeTab, 1, searchTerm);
      }
    },
    [activeTab, searchTerm, loadTabData]
  );

  const handlePageChange = useCallback((tableKey, page) => {
    setCurrentPages((prev) => ({
      ...prev,
      [tableKey]: page,
    }));

    __dashboardMemoryCache.ui.currentPages = {
      ...__dashboardMemoryCache.ui.currentPages,
      [tableKey]: page,
    };

    if (tableKey !== 'students') {
      __dashboardMemoryCache.tabsHydrated[tableKey] = false;
    }

    loadTabData(tableKey, page, searchTerm);
  }, [loadTabData, searchTerm]);

  useEffect(() => {
    if (!searchTerm) return;

    const handler = setTimeout(() => {
      if (activeTab && !['overview', 'batches'].includes(activeTab)) {
        setCurrentPages((prev) => ({ ...prev, [activeTab]: 1 }));
        __dashboardMemoryCache.tabsHydrated[activeTab] = false;
        if (activeTab === 'students') {
          __dashboardMemoryCache.studentPageCache = {};
        }
        loadTabData(activeTab, 1, searchTerm);
      }
    }, 500);

    return () => clearTimeout(handler);
  }, [searchTerm, activeTab, loadTabData]);

  const handleSearch = useCallback(({ term }) => {
    setSearchTerm(term);
  }, []);

  const handleStudentSearchChange = useCallback((term) => {
    const trimmed = (term || '').trim();
    setSearchTerm(trimmed);
    __dashboardMemoryCache.studentPageCache = {};
    if (!trimmed) {
      setCurrentPages((prev) => ({ ...prev, students: 1 }));
      __dashboardMemoryCache.tabsHydrated.students = false;
      loadTabData('students', 1, '');
    }
  }, [loadTabData]);

  const handleStudentsPageChange = useCallback(
    (page) => handlePageChange('students', page),
    [handlePageChange]
  );
  const handleTrainersPageChange = useCallback(
    (page) => handlePageChange('trainers', page),
    [handlePageChange]
  );
  const handleCoursesPageChange = useCallback(
    (page) => handlePageChange('courses', page),
    [handlePageChange]
  );
  /* The foot of the courses list asks for the next page when it comes into
     view. Only the active tab observes, so a hidden list never fires.

     Each of these five depends on its own loadingMore flag as well as
     tabLoading, and that is load-bearing rather than tidy: tabLoading is
     raised only for page 1 (`if (page <= 1)` in loadTabData), so from page
     2 on it never changes, and an effect that does not re-run leaves the
     observer it built in place. An IntersectionObserver fires on a CHANGE
     of intersection -- if the sentinel never left the viewport it will not
     report again, and the list just stops. The per-tab flag goes true when
     the observer asks and false in endTabLoad(), so the effect re-runs on
     completion and the fresh observer reports the foot's current state
     immediately. */
  const coursesHasMore = filteredCourses.length < (tabPagination.courses.total || 0);

  useEffect(() => {
    const node = coursesEndRef.current;
    if (activeTab !== 'courses' || !node || !coursesHasMore || tabLoading || loadingMoreTab) {
      return undefined;
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (!entries.some((e) => e.isIntersecting)) return;
        // `tabLoading` is state and lags a frame, so the ref is what stops
        // one pass asking for two pages.
        if (loadingMoreTabRef.current) return;
        loadingMoreTabRef.current = true;
        setLoadingMoreTab(true);
        handlePageChange('courses', currentPages.courses + 1);
      },
      { rootMargin: '150px' },
    );
    io.observe(node);
    return () => io.disconnect();
  }, [activeTab, coursesHasMore, tabLoading, loadingMoreTab, currentPages.courses, handlePageChange]);

  /* This WAS:
   *
   *   useEffect(() => { if (!tabLoading) { loadingMoreTabRef.current = false; ... } },
   *             [tabLoading]);
   *
   * and it is why every tab stopped paging after the first scroll.
   * loadTabData only raises tabLoading for page 1 (`if (page <= 1)
   * setTabLoading(true)`), so from page 2 on the flag is already false
   * when the finally sets it false again. Setting state to the value it
   * already holds is a bail-out in React: no re-render, so an effect
   * keyed on [tabLoading] never runs again -- and loadingMoreTabRef,
   * raised by the observer before the fetch, was never lowered. Every
   * later observer callback hit `if (loadingMoreTabRef.current) return`.
   *
   * One ref is shared by all five lists, which is why students, trainers,
   * jobs, events and courses all stopped at the same point.
   *
   * The guard is cleared by endTabLoad() at every exit from loadTabData
   * now, which is where the page actually ends. */

  const studentsHasMore =
    filteredStudents.length < (tabPagination.students.total || 0);

  useEffect(() => {
    const node = studentsTabEndRef.current;
    if (activeTab !== 'students' || !node || !studentsHasMore || tabLoading || loadingMoreStudentsTab) {
      return undefined;
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (!entries.some((e) => e.isIntersecting)) return;
        if (loadingMoreTabRef.current) return;
        loadingMoreTabRef.current = true;
        setLoadingMoreStudentsTab(true);
        handlePageChange('students', currentPages.students + 1);
      },
      { rootMargin: '150px' },
    );
    io.observe(node);
    return () => io.disconnect();
  }, [activeTab, studentsHasMore, tabLoading, loadingMoreStudentsTab, currentPages.students, handlePageChange]);

  const jobsHasMore = filteredJobs.length < (tabPagination.jobs.total || 0);

  useEffect(() => {
    const node = jobsEndRef.current;
    if (activeTab !== 'jobs' || !node || !jobsHasMore || tabLoading || loadingMoreJobsTab) {
      return undefined;
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (!entries.some((e) => e.isIntersecting)) return;
        if (loadingMoreTabRef.current) return;
        loadingMoreTabRef.current = true;
        setLoadingMoreJobsTab(true);
        handlePageChange('jobs', currentPages.jobs + 1);
      },
      { rootMargin: '150px' },
    );
    io.observe(node);
    return () => io.disconnect();
  }, [activeTab, jobsHasMore, tabLoading, loadingMoreJobsTab, currentPages.jobs, handlePageChange]);

  const trainersHasMore =
    filteredTrainers.length < (tabPagination.trainers.total || 0);

  useEffect(() => {
    const node = trainersEndRef.current;
    if (activeTab !== 'trainers' || !node || !trainersHasMore || tabLoading || loadingMoreTrainersTab) {
      return undefined;
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (!entries.some((e) => e.isIntersecting)) return;
        if (loadingMoreTabRef.current) return;
        loadingMoreTabRef.current = true;
        setLoadingMoreTrainersTab(true);
        handlePageChange('trainers', currentPages.trainers + 1);
      },
      { rootMargin: '150px' },
    );
    io.observe(node);
    return () => io.disconnect();
  }, [activeTab, trainersHasMore, tabLoading, loadingMoreTrainersTab, currentPages.trainers, handlePageChange]);

  const handleJobsPageChange = useCallback(
    (page) => handlePageChange('jobs', page),
    [handlePageChange]
  );
  const handleEventsPageChange = useCallback(
    (page) => handlePageChange('events', page),
    [handlePageChange]
  );
  const handleBatchesPageChange = useCallback(
    (page) => handlePageChange('batches', page),
    [handlePageChange]
  );

  const handleViewFullJD = useCallback((job) => {
    setSelectedJobForFullJD(job);
    setShowFullJDModal(true);
  }, []);

  const fetchAllData = useCallback(async (forceRefresh = false) => {
    if (isFetchingRef.current) return;
    if (__dashboardMemoryCache.hydrated && !forceRefresh) return;
    isFetchingRef.current = true;
    setError(null);
    setLoading(true);
    setApiErrors({
      batches: null,
      courses: null,
      students: null,
      trainers: null,
      events: null,
      jobs: null,
    });

    try {
      // The Overview skeleton is this load's indicator, so the global
      // overlay stays down for it (Loader/busy.js). Actions still raise it.
      silenceFor();
      const [overview, metricsResponse] = await Promise.all([
        getDashboardOverview({ scope: 'admin', recentLimit: 5 }),
        getDashboardMetrics(),
      ]);
      if (!isMountedRef.current) return;

      const payload = overview?.data || {};
      const counts = metricsResponse?.data?.counts || {};

      /* Every figure here is the API's. The page used to make Completed
         batches up as total - active, fall back through a dozen field names
         and then to the length of whatever list it held, and sort the
         overview's receipts itself. A missing count reads 0, not a guess. */
      const nextDashboard = {
        recentBatches: Array.isArray(payload.batches) ? payload.batches : [],
        recentEvents: Array.isArray(payload.events) ? payload.events : [],
        recentJobs: Array.isArray(payload.jobs) ? payload.jobs : [],
      };
      const nextStats = {
        totalBatches: counts.totalBatches ?? 0,
        activeBatches: counts.activeBatches ?? 0,
        completedBatches: counts.completedBatches ?? 0,
        totalCourses: counts.totalCourses ?? 0,
        totalStudents: counts.totalStudents ?? 0,
        totalTrainers: counts.activeTrainers ?? 0,
        totalEvents: counts.events ?? 0,
        totalJobs: counts.jobOpenings ?? 0,
      };

      setDashboardData((prev) => ({ ...prev, ...nextDashboard }));
      setStats(nextStats);
      setTabPagination((prev) => ({
        ...prev,
        batches: { total: nextStats.totalBatches },
        courses: { total: nextStats.totalCourses },
        students: { total: nextStats.totalStudents },
        trainers: { total: nextStats.totalTrainers },
        events: { total: nextStats.totalEvents },
        jobs: { total: nextStats.totalJobs },
      }));

      __dashboardMemoryCache.hydrated = true;
      __dashboardMemoryCache.dashboardData = nextDashboard;
      __dashboardMemoryCache.stats = nextStats;
    } catch (err) {
      if (isMountedRef.current) {
        console.error('Dashboard fetch error:', err);
        setError(err?.message || 'Failed to load dashboard data. Please try again.');
      }
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
      isFetchingRef.current = false;
    }
  }, []);




  useEffect(() => {
    if (__dashboardMemoryCache.hydrated) {
      setActiveTab(__dashboardMemoryCache.ui.activeTab || 'overview');
      setCurrentPages(
        __dashboardMemoryCache.ui.currentPages || {
          batches: 1,
          courses: 1,
          students: 1,
          trainers: 1,
          jobs: 1,
          events: 1,
        }
      );
      setSearchTerm(__dashboardMemoryCache.ui.searchTerm || '');
      setSelectedBatchFilter(__dashboardMemoryCache.ui.selectedBatchFilter);
      setSelectedCourseFilter(__dashboardMemoryCache.ui.selectedCourseFilter);
      setSelectedStudentFilter(__dashboardMemoryCache.ui.selectedStudentFilter);
      setSelectedTrainerFilter(__dashboardMemoryCache.ui.selectedTrainerFilter);
      setSelectedJobFilter(__dashboardMemoryCache.ui.selectedJobFilter);
      setSelectedEventFilter(__dashboardMemoryCache.ui.selectedEventFilter);

      setDashboardData((prev) => ({ ...prev, ...__dashboardMemoryCache.dashboardData }));
      setStats(__dashboardMemoryCache.stats);

      // The overview is kept; the tab lists are not, so each tab asks the
      // API again when opened instead of showing an empty table whose
      // hydrated flag says it is already loaded.
      __dashboardMemoryCache.tabsHydrated = {
        batches: false, courses: false, students: false,
        trainers: false, jobs: false, events: false,
      };
      __dashboardMemoryCache.studentPageCache = {};

      setLoading(false);
    } else {
      fetchAllData();
    }
  }, [fetchAllData]);

  // Load tab data when user switches tabs or after an overview refresh
  useEffect(() => {
    if (loading || !activeTab || activeTab === 'overview') return;
    if (!['courses', 'students', 'trainers', 'jobs', 'events'].includes(activeTab)) return;
    const page = currentPages[activeTab] || 1;

    // Only trigger load when tab is not hydrated or loading finished
    loadTabData(activeTab, page, searchTerm);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, loading]);

  /* The Batches tab asks the API for exactly the rows it shows: the Active /
     Completed toggle and the batch picker travel as filters. A change to
     either is a different list, so it is fetched again from page 1 -- the
     overview tiles set the toggle on their way here, and this is what makes
     the tab open on the rows the tile counted. */
  const batchQueryKey = `${batchFilter}|${selectedBatchFilter?.value || ''}`;
  const lastBatchQueryRef = useRef(batchQueryKey);
  useEffect(() => {
    if (loading || activeTab !== 'batches') return;
    if (lastBatchQueryRef.current !== batchQueryKey) {
      lastBatchQueryRef.current = batchQueryKey;
      __dashboardMemoryCache.tabsHydrated.batches = false;
    }
    loadTabData('batches', 1, '');
  }, [activeTab, loading, batchQueryKey, loadTabData]);


  useEffect(() => {
    // Reset all pagination when search term changes to avoid empty pages
    setCurrentPages({
      batches: 1,
      courses: 1,
      students: 1,
      trainers: 1,
      jobs: 1,
      events: 1,
    });
  }, [searchTerm]);

  useEffect(() => {
    __dashboardMemoryCache.ui = {
      ...__dashboardMemoryCache.ui,
      activeTab,
      currentPages,
      searchTerm,
      selectedBatchFilter,
      selectedCourseFilter,
      selectedStudentFilter,
      selectedTrainerFilter,
      selectedJobFilter,
      selectedEventFilter,
    };
  }, [
    activeTab,
    currentPages,
    searchTerm,
    selectedBatchFilter,
    selectedCourseFilter,
    selectedStudentFilter,
    selectedTrainerFilter,
    selectedJobFilter,
    selectedEventFilter,
  ]);









  const handleBatchClick = useCallback(async (batch) => {
    setSelectedBatch(batch);
    // Always opens on Details; the roster is a tab away.
    setBatchModalTab('details');
    setShowBatchModal(true);

    try {
      // Fetch actual enrolled students for this batch
      const response = await getBatchedStudents({
        batchId: batch.id,
        limit: 100
      });

      if (response && response.data) {
        setSelectedBatch(prev => ({
          ...prev,
          students: response.data
        }));
      }
    } catch (error) {
      console.error("Failed to fetch batch students:", error);
      // Fallback: stay with whatever data was already in the batch object
    }
  }, []);

  const formatDate = useCallback((dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  }, []);

  const formatTime = useCallback((dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return 'N/A';
    }
  }, []);

  const getStatusBadge = useCallback(() => {
    return null;
  }, []);

  const handleStudentClick = async (student, batchId) => {
    setSelectedStudentData({
      student: student,
      batchId: batchId,
      batchName: selectedBatch?.name || selectedBatch?.batchName || 'N/A',
      loading: true
    });
    setShowStudentModal(true);

    try {
      const response = await getFullStudent({
        studentId: student.id || student.studentId || student._id,
        batchId
      });

      if (response && response.data) {
        setSelectedStudentData(prev => ({
          ...prev,
          student: {
            ...prev.student,
            ...response.data.student,
            // Ensure names and emails are mapped correctly for the modal
            studentName: response.data.student.name || response.data.student.studentName || prev.student.name,
            studentEmail: response.data.student.email || response.data.student.studentEmail || prev.student.email,
            phone: response.data.student.phone || response.data.student.studentNumber || prev.student.phone
          },
          // Counted by the API over every session of the batch, in the
          // shape the popup reads (utils/studentAttendanceSummary.js).
          attendanceSummary: response.data.attendanceSummary || null,
          loading: false
        }));
      } else {
        setSelectedStudentData(prev => ({ ...prev, loading: false }));
      }
    } catch (error) {
      console.error("Failed to fetch full student info:", error);
      setSelectedStudentData(prev => ({ ...prev, loading: false }));
    }
  };

  const getModeBadge = useCallback((mode) => {
    const modeColors = {
      Online: 'primary',
      Offline: 'info',
      Hybrid: 'warning',
    };
    return (
      <span>
        {mode || 'N/A'}
      </span>
    );
  }, []);



  const eventsHasMore =
    dashboardData.events.length < (tabPagination.events.total || 0);

  useEffect(() => {
    const node = eventsEndRef.current;
    if (activeTab !== 'events' || !node || !eventsHasMore || tabLoading || loadingMoreEventsTab) {
      return undefined;
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (!entries.some((e) => e.isIntersecting)) return;
        if (loadingMoreTabRef.current) return;
        loadingMoreTabRef.current = true;
        setLoadingMoreEventsTab(true);
        handlePageChange('events', currentPages.events + 1);
      },
      { rootMargin: '150px' },
    );
    io.observe(node);
    return () => io.disconnect();
  }, [activeTab, eventsHasMore, tabLoading, loadingMoreEventsTab, currentPages.events, handlePageChange]);

  const getJobStatusBadgeVariant = useCallback((status) => {
    if (status?.toLowerCase() === 'inactive') {
      return 'danger';
    }
    return 'success';
  }, []);

  // View functions for different entities (memoized)
  /* One student, in full, counted by the API (GET /dashboard/student-view/:id):
     their batches, attendance tallies and quiz history. This used to fetch
     every attendance record in the system, every quiz and every batch quiz
     history, and tally this student's share of them here -- after merging
     whatever student rows the page happened to have loaded. A second open
     while the first is on its way is ignored, not queued. */
  const viewingStudentRef = useRef(false);
  const handleViewStudent = useCallback(async (studentId) => {
    if (!studentId || viewingStudentRef.current) return;
    viewingStudentRef.current = true;
    try {
      const response = await getDashboardStudentView(studentId);
      const view = response?.data;
      if (!view?.student) {
        toast.error('Student record could not be found. Try refreshing the data.');
        return;
      }
      setSelectedStudentData({ ...view, loading: false });
      setShowStudentModal(true);
    } catch (error) {
      console.error('Error in handleViewStudent:', error);
      toast.error('Failed to fetch student details. Please try again.');
    } finally {
      viewingStudentRef.current = false;
    }
  }, []);

  // The row is the record; it used to be looked up again by id in whichever
  // page of trainers the page happened to hold, and missed anything earlier.
  /* The popup opens at once on the row, then fills in how to reach the
     trainer: the phone is not stored on the trainer, it is on their login
     account, which the API looks up by the trainer's userId. Only the
     answer for the trainer still showing is applied, so a quick second
     click is never overwritten by the first one's reply. */
  const trainerDetailSeq = useRef(0);
  const handleViewTrainer = useCallback(async (trainer) => {
    if (!trainer) return;
    const mine = ++trainerDetailSeq.current;
    setTrainerDetails({ ...trainer, contact: undefined });
    try {
      const res = await getDashboardTabDetail('trainers', trainer.id);
      if (trainerDetailSeq.current !== mine) return;
      setTrainerDetails((prev) =>
        prev && prev.id === trainer.id
          ? { ...prev, ...(res?.data?.record || {}), contact: res?.data?.record?.contact ?? null }
          : prev,
      );
    } catch (_) {
      if (trainerDetailSeq.current !== mine) return;
      setTrainerDetails((prev) => (prev && prev.id === trainer.id ? { ...prev, contact: null } : prev));
    }
  }, []);

  const handleViewJob = useCallback(
    async (jobId) => {
      try {
        const response = await getJobById(jobId);
        const job = response.data;

        const formatJobDate = (dateString) => {
          return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
          });
        };

        setJobDetails(job);
      } catch (error) {
        toast.error('Failed to fetch job details');
      }
    },
    [getJobStatusBadgeVariant]
  );




  // The row already carries trainerNames and batchNames, resolved by the API.
  const handleViewEvent = useCallback((event) => {
    setEventTab('details');
    setEventDetails(event);
  }, []);

  const courseHeaders = useMemo(
    () => [
      {
        title: 'Course',
        prop: 'name',
        sortable: true,
        cell: (row) => (
          <div className="lp-cell">
            <span className="lp-name">{row.name}</span>
            {row.code && <span className="lp-sub">{row.code}</span>}
          </div>
        ),
      },
      {
        title: 'Duration',
        prop: 'duration',
        cell: (row) => <span className="lp-plain">{row.duration || '—'}</span>,
      },
      {
        title: 'Branch',
        prop: 'branch',
        cell: (row) => (
          <div className="lp-cell">
            <span className="lp-plain">{row.branchOfCourse || '—'}</span>
            {row.departmentOfCourse && (
              <span className="lp-sub">{row.departmentOfCourse}</span>
            )}
          </div>
        ),
      },
      {
        title: 'Topics',
        prop: 'syllabusTopicCount',
        sortable: true,
        cell: (row) => (
          <span className="lp-num">{row.syllabusTopicCount || 0}</span>
        ),
      },
      {
        title: 'Last topic',
        prop: 'syllabusTopicPreview',
        cell: (row) => (
          <span className="lp-plain">{row.syllabusTopicPreview || '—'}</span>
        ),
      },
    ],
    []
  );

  const studentHeaders = useMemo(
    () => [
      {
        title: 'Student',
        prop: 'name',
        sortable: true,
        cell: (row) => (
          <div className="lp-cell">
            <span className="lp-name">{row.name || 'Unnamed'}</span>
            {row.studentIdNumber && (
              <span className="lp-sub">{row.studentIdNumber}</span>
            )}
          </div>
        ),
      },
      {
        title: 'Email',
        prop: 'email',
        cell: (row) => (
          <span className="lp-contact">{row.email || '—'}</span>
        ),
      },
      {
        title: 'Phone',
        prop: 'phone',
        cell: (row) => (
          <span className="lp-contact lp-contact--tel">
            {row.phone || '—'}
          </span>
        ),
      },
      {
        title: 'Courses',
        prop: 'course',
        cell: (row) => {
          const names = Array.isArray(row.courseNames) ? row.courseNames : [];
          if (names.length === 0) {
            return <span className="lp-muted">—</span>;
          }
          // Two named, the rest counted, so one student on six courses
          // cannot push every other column off the table.
          return (
            <span className="lp-plain" title={names.join(', ')}>
              {names.slice(0, 2).join(', ')}
              {names.length > 2 && (
                <span className="lp-more">+{names.length - 2} more</span>
              )}
            </span>
          );
        },
      },
    ],
    []
  );

  const trainerHeaders = useMemo(
    () => [
      {
        title: 'Trainer',
        prop: 'trainerName',
        sortable: true,
        cell: (row) => (
          <div className="lp-cell">
            <span className="lp-name">
              {row.trainerName || row.name || 'Unnamed'}
            </span>
            {(row.specialization || row.expertise) && (
              <span className="lp-sub">
                {row.specialization || row.expertise}
              </span>
            )}
          </div>
        ),
      },
      {
        title: 'Email',
        prop: 'trainerEmail',
        cell: (row) => (
          <span className="lp-contact">
            {row.trainerEmail || row.traineremail || row.email || '—'}
          </span>
        ),
      },
      // No Phone column: trainers have no phone of their own. It is on their
      // login account, and the trainer popup looks it up when opened.
      {
        title: 'Experience',
        prop: 'experience',
        sortable: true,
        cell: (row) => (
          <span className="lp-num">
            {row.experience ? `${row.experience} yr` : '—'}
          </span>
        ),
      },
    ],
    []
  );

  const jobHeaders = useMemo(
    () => [
      {
        title: 'Role',
        prop: 'jobName',
        sortable: true,
        cell: (row) => (
          <div className="lp-cell">
            <span className="lp-name">{row.jobName || 'Untitled'}</span>
            {(row.organizationName || row.company) && (
              <span className="lp-sub">
                {row.organizationName || row.company}
              </span>
            )}
          </div>
        ),
      },
      {
        title: 'Location',
        prop: 'jobLocation',
        cell: (row) => (
          <span className="lp-plain">{row.jobLocation || '—'}</span>
        ),
      },
      {
        title: 'Salary',
        prop: 'salary',
        cell: (row) => (
          <span className="lp-plain">{row.salary || 'Confidential'}</span>
        ),
      },
      {
        title: 'Posted',
        prop: 'createdAt',
        sortable: true,
        cell: (row) => (
          <span className="lp-when">
            {row.createdAt
              ? new Date(row.createdAt).toLocaleDateString()
              : '—'}
          </span>
        ),
      },
      {
        title: 'Status',
        prop: 'status',
        sortable: true,
        cell: (row) => {
          const open = String(row.status || '').toLowerCase() === 'active';
          return (
            <span className={`pg-pill ${open ? 'pg-pill--good' : ''}`.trim()}>
              {row.status || 'Unknown'}
            </span>
          );
        },
      },
    ],
    []
  );

  const eventHeaders = useMemo(
    () => [
      {
        title: 'Event',
        prop: 'name',
        sortable: true,
        cell: (row) => (
          <div className="lp-cell">
            <span className="lp-name">{row.name || 'Untitled'}</span>
            <span className="lp-sub">{row.category || 'General'}</span>
          </div>
        ),
      },
      {
        title: 'When',
        prop: 'date',
        sortable: true,
        cell: (row) => (
          <div className="lp-cell">
            <span className="lp-when">{formatDate(row.date)}</span>
            {row.time && (
              <span className="lp-sub">
                {new Date(row.time).toLocaleTimeString(undefined, {
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </span>
            )}
          </div>
        ),
      },
      {
        title: 'Where',
        prop: 'venueId',
        cell: (row) => (
          <span className="lp-plain">
            {row.classroomNumber
              ? `Room ${row.classroomNumber}`
              : row.onlineLink && row.onlineLink !== 'N/A'
                ? 'Online'
                : row.offlineAddress || '—'}
          </span>
        ),
      },
      {
        title: 'Trainers',
        prop: 'trainerId',
        cell: (row) => {
          const clean = Array.isArray(row.trainerNames) ? row.trainerNames : [];
          if (clean.length === 0) return <span className="lp-muted">—</span>;
          // Two named, the rest counted, so one event covering twenty
          // trainers cannot push every other column off the table.
          return (
            <span className="lp-plain" title={clean.join(', ')}>
              {clean.slice(0, 2).join(', ')}
              {clean.length > 2 && (
                <span className="lp-more">+{clean.length - 2} more</span>
              )}
            </span>
          );
        },
      },
      {
        title: 'Organiser',
        prop: 'organizerName',
        cell: (row) => (
          <div className="lp-cell">
            <span className="lp-plain">{row.organizerName || '—'}</span>
            {row.organizerPhone && (
              <span className="lp-sub">{row.organizerPhone}</span>
            )}
          </div>
        ),
      },
    ],
    [formatDate]
  );

  // Only show critical error if entire system fails
  if (error && Object.keys(apiErrors).every((key) => apiErrors[key] !== null)) {
    return (
      <div>
        <div>
          <h4>Error Loading Dashboard</h4>
          <p>{error}</p>
          <button type="button" onClick={fetchAllData}>
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // The seven figures the dashboard opens on. Label above value, one tone
  // each as a 2px top rule — the same sm-vital card the trainer dashboard
  // uses, so the two read as one product.
  const vitals = [
    {
      icon: <GraduationCap size={20} />,
      label: 'Total batches',
      value: stats.totalBatches,
      tone: 'sky',
      chips: [
        stats.activeBatches ? { text: `${stats.activeBatches} active` } : null,
        stats.completedBatches ? { text: `${stats.completedBatches} completed` } : null,
      ].filter(Boolean),
      hint: 'No batches yet',
    },
    {
      icon: <Users size={20} />,
      label: 'Total students',
      value: stats.totalStudents,
      tone: 'indigo',
      hint: 'No students yet',
    },
    {
      icon: <UserCheck size={20} />,
      label: 'Trainers',
      value: stats.totalTrainers,
      tone: 'teal',
      hint: 'No trainers yet',
    },
    {
      icon: <BookOpen size={20} />,
      label: 'Courses',
      value: stats.totalCourses,
      tone: 'gold',
      hint: 'No courses yet',
    },
    {
      icon: <Building size={20} />,
      label: 'Job openings',
      value: stats.totalJobs,
      tone: 'berry',
      hint: 'No jobs posted',
    },
    {
      icon: <Calendar size={20} />,
      label: 'Events',
      value: stats.totalEvents,
      tone: 'sky',
      hint: 'No events scheduled',
    },
  ];

  /* The first load, or a Refresh that cleared the cache: there is no data
     to show yet, so the Overview draws its skeleton and the tab counts
     wait rather than reading 0. */
  const showOverviewSkeleton = loading && !__dashboardMemoryCache.hydrated;
  // The tab belongs to the access-center feature, so it is hidden with it
  // while that is listed in COMING_SOON_FEATURES.
  const canReviewAccess = !isComingSoon('access-center') && (
    user?.isSuperAdmin === true ||
    user?.isSuperAdmin === 'true' ||
    String(user?.profileIds || '').toLowerCase() === 'admin' ||
    (user?.profiles || []).some((p) => ['admin', 'superadmin'].includes(String(p?.label || '').toLowerCase())));

  const TABS = [
    { key: 'overview', label: 'Overview' },
    { key: 'batches', label: 'Batches', count: stats.totalBatches },
    { key: 'courses', label: 'Courses', count: stats.totalCourses },
    { key: 'students', label: 'Students', count: stats.totalStudents },
    { key: 'trainers', label: 'Trainers', count: stats.totalTrainers },
    { key: 'jobs', label: 'Jobs', count: stats.totalJobs },
    { key: 'events', label: 'Events', count: stats.totalEvents },
    // Deciding on role requests writes roles, so only a full administrator
    // sees it -- the same test the API applies (accessrequest.service isAdmin).
    ...(canReviewAccess ? [{ key: 'access', label: 'Access' }] : []),
  ];

  return (
    <div className="dashboard-wrapper">
      <div className="td-page">
        <BusinessPulse user={user} getDashboardTitle={getDashboardTitle} />

        {/* How the teaching is landing, directly under the welcome box.
            It was on the Batches tab beside the attendance report, which
            reads well but puts the one figure worth acting on behind a
            click -- and the trainers who need looking at are not a
            batches question. */}
        <TrainerFeedbackReport />

        <div className="bp-refresh">
          {/* Session feedback lives on its own page -- it is the whole
              institution's teaching quality and is read deliberately, not
              glanced at. This is the way in from the dashboard: the
              sidebar entry alone meant knowing to look under LMS for it. */}
          <button
            type="button"
            className="td-head-action"
            onClick={() => navigate('/session-feedback')}
          >
            <Star size={15} />
            Session feedback
          </button>
          <button
            type="button"
            className="td-head-action"
            onClick={() => {
              // Reset ALL caches so fetchAllData re-fetches everything
              __dashboardMemoryCache.hydrated = false;
              __dashboardMemoryCache.tabsHydrated = {
                batches: false, courses: false, students: false,
                trainers: false, jobs: false, events: false,
              };
              fetchAllData(true);
            }}
            disabled={loading}
          >
            <TrendingUp size={15} />
            {loading ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>

        <div className="td-tabs">
          <div className="td-tabs-strip" role="tablist">
            {TABS.map((tab) => (
              <button
                type="button"
                key={tab.key}
                role="tab"
                aria-selected={activeTab === tab.key}
                className={`td-tab${activeTab === tab.key ? ' is-on' : ''}`}
                onClick={() => setActiveTab(tab.key)}
              >
                {tab.label}
                {tab.count !== undefined && !showOverviewSkeleton && (
                  <span className="td-tab-n">{tab.count}</span>
                )}
              </button>
            ))}
          </div>

          <div className="td-tabs-body">

              {/* Overview Tab */}
              {activeTab === 'overview' && showOverviewSkeleton && <AdminOverviewSkeleton />}
              {activeTab === 'overview' && !showOverviewSkeleton && (
                <div className="ov">
                  <ErrorAlert error={apiErrors.batches} section="overview" />

                  {/* Six readouts. Label above figure, one hairline frame,
                      no tinted circle behind the icon. */}
                  {/* Each figure goes to the tab that lists what it counts,
                      and the two batch tiles set the active/completed toggle
                      on the way so the tab opens on exactly the rows the
                      number was counting -- a tile reading 12 that lands on
                      a list of 40 is worse than one that does not move.

                      Deliberately NOT Waypoint: these are global totals, and
                      Waypoint addresses one entity by id. There is no "all
                      students" frame, and inventing one would put a modal
                      list in front of a tab that already does the job
                      better. Waypoint is for drilling into a record; a tab
                      is for browsing a collection. */}
                  <div className="ov-tiles">
                    {/* Live, from the socket's presence set rather than a
                        total in the overview payload -- every online
                        student in the institute. See OnlineStudentsBox.
                        The trainer tile beside it reads the same presence
                        set against the trainer directory. */}
                    <OnlineStudentsBox scope="admin" variant="tile" />
                    <OnlineTrainersBox />
                    {[
                      { icon: CheckCircle, label: 'Active batches', value: stats.activeBatches, tab: 'batches', batches: 'active' },
                      { icon: Award, label: 'Completed batches', value: stats.completedBatches, tab: 'batches', batches: 'completed' },
                      { icon: Users, label: 'Total students', value: stats.totalStudents, tab: 'students' },
                      { icon: UserCheck, label: 'Active trainers', value: stats.totalTrainers, tab: 'trainers' },
                      { icon: Calendar, label: 'Events', value: stats.totalEvents, tab: 'events' },
                      { icon: Building, label: 'Job openings', value: stats.totalJobs, tab: 'jobs' },
                    ].map(({ icon: Icon, label, value, tab, batches }) => {
                      // A tile with nothing behind it stays a plain figure
                      // rather than opening an empty tab.
                      const go =
                        (value ?? 0) > 0
                          ? () => {
                              if (batches) setBatchFilter(batches);
                              setActiveTab(tab);
                            }
                          : null;
                      const Tag = go ? 'button' : 'div';
                      return (
                        <Tag
                          className={`ov-tile${go ? ' ov-tile--open' : ''}`}
                          key={label}
                          {...(go
                            ? { type: 'button', onClick: go, title: `Show ${label.toLowerCase()}` }
                            : {})}
                        >
                          <span className="ov-tile-label">
                            <Icon size={13} aria-hidden="true" />
                            {label}
                          </span>
                          <span className="ov-tile-value">{value ?? 0}</span>
                        </Tag>
                      );
                    })}
                  </div>

                  <div className="ov-duo">
                    {/* ── Recent batches ── */}
                    <section className="ov-panel">
                      <header className="ov-panel-head">
                        <h2 className="ov-panel-title">Recent batches</h2>
                        <button
                          type="button"
                          className="ov-panel-link"
                          onClick={() => navigate('/batches')}
                        >
                          All batches
                        </button>
                      </header>

                      {dashboardData.recentBatches.length === 0 ? (
                        <p className="ov-empty">No batches yet.</p>
                      ) : (
                        <ul className="ov-list">
                          {dashboardData.recentBatches.map((batch, index) => {
                            // Upcoming / Active / Completed, decided by the API.
                            const state = batch.status;
                            const tone =
                              state === 'Active'
                                ? 'pg-pill--good'
                                : state === 'Upcoming'
                                  ? 'pg-pill--warn'
                                  : '';
                            return (
                              <li key={index}>
                                {/* Waypoint. This used to open the dashboard's
                                    own batch popup, which is a dead end: it
                                    names a course and a trainer and reaches
                                    neither. The bridge opens the same record
                                    and keeps going. */}
                                <button
                                  type="button"
                                  className="ov-row"
                                  onClick={() => openEntity('batch', batch.id || batch._id)}
                                >
                                  <span className="ov-row-mark" aria-hidden="true">
                                    {batch.name?.charAt(0) || 'B'}
                                  </span>
                                  <span className="ov-row-main">
                                    <span className="ov-row-name">{batch.name}</span>
                                    <span className="ov-row-sub">
                                      {batch.courseDetails?.name || 'No course'} ·{' '}
                                      {formatTime(batch.startTime || batch.startDate)} –{' '}
                                      {formatTime(batch.endTime || batch.endDate)}
                                    </span>
                                  </span>
                                  <span className={`pg-pill ${tone}`.trim()}>{state}</span>
                                </button>
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </section>

                    {/* ── Recent events ── */}
                    <section className="ov-panel">
                      <header className="ov-panel-head">
                        <h2 className="ov-panel-title">Recent events</h2>
                        <button
                          type="button"
                          className="ov-panel-link"
                          onClick={() => navigate('/list-event-form')}
                        >
                          All events
                        </button>
                      </header>

                      {dashboardData.recentEvents.length === 0 ? (
                        <p className="ov-empty">Nothing scheduled.</p>
                      ) : (
                        <ul className="ov-list">
                          {dashboardData.recentEvents.map((event, index) => {
                            /* No batches on the row. An event can carry
                               a dozen, and joined into the sub line they
                               ran the row out of room; the row opens the
                               event, whose Batches tab lists them. */
                            return (
                              <li key={index}>
                                {/* Waypoint. These five rows named an event, a
                                    batch and an organiser and went nowhere --
                                    ov-row--static is exactly that, a row with
                                    no destination. The event frame carries the
                                    trainers and batches on its own tabs. */}
                                {(() => {
                                  const eid = event.id || event._id;
                                  const Tag = eid ? 'button' : 'div';
                                  return (
                                    <Tag
                                      className={`ov-row${eid ? '' : ' ov-row--static'}`}
                                      {...(eid
                                        ? {
                                            type: 'button',
                                            onClick: () => openEntity('event', eid),
                                            title: `Open ${event.name || 'this event'}`,
                                          }
                                        : {})}
                                    >
                                      <span className="ov-row-mark" aria-hidden="true">
                                        {event.name?.charAt(0) || 'E'}
                                      </span>
                                      <span className="ov-row-main">
                                        <span className="ov-row-name">{event.name}</span>
                                        <span className="ov-row-sub">
                                          {formatDate(event.date)} ·{' '}
                                          {event.time
                                            ? new Date(event.time).toLocaleTimeString(undefined, {
                                                hour: '2-digit',
                                                minute: '2-digit',
                                              })
                                            : 'All day'}
                                        </span>
                                      </span>
                                      {event.organizerName && (
                                        <span className="ov-row-side">{event.organizerName}</span>
                                      )}
                                    </Tag>
                                  );
                                })()}
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </section>
                  </div>

                  {/* ── Recent jobs ── */}
                  <section className="ov-panel">
                    <header className="ov-panel-head">
                      <h2 className="ov-panel-title">Recent jobs</h2>
                      <button
                        type="button"
                        className="ov-panel-link"
                        onClick={() => navigate('/job-section')}
                      >
                        All jobs
                      </button>
                    </header>

                    {dashboardData.recentJobs.length === 0 ? (
                      <p className="ov-empty">No jobs posted.</p>
                    ) : (
                      <ul className="ov-list ov-list--grid">
                        {dashboardData.recentJobs.map((job, index) => (
                          <li key={index}>
                            {/* Waypoint, same as the events beside it. */}
                            {(() => {
                              const jid = job.id || job._id;
                              const Tag = jid ? 'button' : 'div';
                              return (
                                <Tag
                                  className={`ov-row${jid ? '' : ' ov-row--static'}`}
                                  {...(jid
                                    ? {
                                        type: 'button',
                                        onClick: () => openEntity('job', jid),
                                        title: `Open ${job.jobName || 'this job'}`,
                                      }
                                    : {})}
                                >
                                  <span className="ov-row-mark" aria-hidden="true">
                                    {job.organizationName?.charAt(0) || 'J'}
                                  </span>
                                  <span className="ov-row-main">
                                    <span className="ov-row-name">{job.jobName}</span>
                                    <span className="ov-row-sub">
                                      {job.organizationName} · {job.jobLocation}
                                    </span>
                                  </span>
                                  <span className="ov-row-side">
                                    {job.salary || 'Confidential'}
                                  </span>
                                </Tag>
                              );
                            })()}
                          </li>
                        ))}
                      </ul>
                    )}
                  </section>
                </div>
              )}

              {activeTab === 'batches' && (
                <div className="ov">
                  <ErrorAlert error={apiErrors.batches} section="batches" />

                  {/* Which batches are turning up, before any one batch is
                      opened. */}
                  <BatchAttendanceReport />

                  <section className="ov-panel">
                    <header className="ov-panel-head">
                      <h2 className="ov-panel-title">Batches</h2>
                      <button
                        type="button"
                        className="ov-panel-link"
                        onClick={() => navigate('/batches')}
                      >
                        All batches
                      </button>
                    </header>

                    <div className="ba-controls">
                      {/* Active or completed — the question this list is read
                          through, so a pair of buttons rather than a select. */}
                      <div className="ba-seg" role="group" aria-label="Batch state">
                        <button
                          type="button"
                          aria-pressed={batchFilter !== 'completed'}
                          className={`ba-seg-btn${batchFilter !== 'completed' ? ' is-on' : ''}`}
                          onClick={() => setBatchFilter('active')}
                        >
                          Active
                        </button>
                        <button
                          type="button"
                          aria-pressed={batchFilter === 'completed'}
                          className={`ba-seg-btn${batchFilter === 'completed' ? ' is-on' : ''}`}
                          onClick={() => setBatchFilter('completed')}
                        >
                          Completed
                        </button>
                      </div>

                      <div className="ba-picker">
                        <AsyncSelectDropdown
                          options={dropdownBatchOptions}
                          value={selectedBatchFilter ? [selectedBatchFilter] : []}
                          onChange={(selected) =>
                            setSelectedBatchFilter(
                              selected && selected.length > 0 ? selected[0] : null,
                            )
                          }
                          onInputChange={handleDropdownBatchSearch}
                          onMenuScrollToBottom={handleDropdownBatchLoadMore}
                          onMenuOpen={handleDropdownBatchOpen}
                          isLoading={loadingDropdownBatches}
                          placeholder="Filter by batch..."
                          isMulti={false}
                          isSearchable={true}
                          searchInControl={true}
                        />
                      </div>
                    </div>

                    {(() => {
                      // Already the toggle's rows -- the API filtered them.
                      const batchesToShow = enhancedBatches;

                      if (batchesToShow.length === 0) {
                        return (
                          <p className="ov-empty">
                            No {batchFilter === 'completed' ? 'completed' : 'active'} batches.
                          </p>
                        );
                      }

                      return (
                        <ul className="ov-list">
                          {batchesToShow.map((batch, index) => {
                            const state = batch.status;
                            const tone =
                              state === 'Active'
                                ? 'pg-pill--good'
                                : state === 'Upcoming'
                                  ? 'pg-pill--warn'
                                  : '';
                            return (
                              <li key={batch.id || index}>
                                <button
                                  type="button"
                                  className="ov-row"
                                  onClick={() => handleBatchClick(batch)}
                                >
                                  <span className="ov-row-mark" aria-hidden="true">
                                    {batch.name?.charAt(0) || 'B'}
                                  </span>
                                  <span className="ov-row-main">
                                    <span className="ov-row-name">{batch.name}</span>
                                    <span className="ov-row-sub">
                                      {batch.courseDetails?.name || batch.courseName || 'No course'}
                                      {' · '}
                                      {formatDate(batch.startTime || batch.startDate)} –{' '}
                                      {formatDate(batch.endTime || batch.endDate)}
                                    </span>
                                  </span>
                                  <span className="ov-row-side">
                                    {batch.studentCount ?? 0} student
                                    {batch.studentCount === 1 ? '' : 's'}
                                  </span>
                                  <span className={`pg-pill ${tone}`.trim()}>{state}</span>
                                </button>
                              </li>
                            );
                          })}
                        </ul>
                      );
                    })()}
                  </section>
                </div>
              )}

              {activeTab === 'courses' && (
                <div className="ov">
                  <ErrorAlert error={apiErrors.courses} section="courses" />

                  <section className="ov-panel ov-panel--flush">
                    <header className="ov-panel-head">
                      <h2 className="ov-panel-title">
                        Courses
                        <span className="ov-panel-count">
                          {tabPagination.courses.total}
                        </span>
                      </h2>
                      <button
                        type="button"
                        className="ov-panel-link"
                        onClick={() => navigate('/list-course')}
                      >
                        All courses
                      </button>
                    </header>

                    <div className="td-list td-list--lms">
                      <NewCustomDataTable
                        data={filteredCourses}
                        headers={courseHeaders}
                        totalRecords={tabPagination.courses.total}
                        currentPage={currentPages.courses}
                        recordsPerPage={itemsPerPage}
                        onPageChange={handleCoursesPageChange}
                        onRowsPerPageChange={handleRowsPerPageChange}
                        loading={tabLoading}
                        loadingMore={loadingMoreTab}
                        serverSide={true}
                        showToolbar={false}
                        showPagination={false}
                        showFooter={false}
                        onRowClick={(row) => setCourseDetails(row)}
                        validRowClickTagNames="*"
                      />
                    </div>

                    {/* Where the reader is, and the sentinel the observer
                        watches. */}
                    <div className="att-picklist-foot" ref={coursesEndRef}>
                      {loadingMoreTab ? (
                        <>
                          <span className="pg-spinner att-spinner" aria-hidden="true" />
                          <span>Loading more courses...</span>
                        </>
                      ) : coursesHasMore ? (
                        <span>
                          {filteredCourses.length} of {tabPagination.courses.total} courses
                        </span>
                      ) : (
                        tabPagination.courses.total > 0 && (
                          <span>
                            All {tabPagination.courses.total} course
                            {tabPagination.courses.total === 1 ? '' : 's'} loaded
                          </span>
                        )
                      )}
                    </div>
                  </section>
                </div>
              )}

              {activeTab === 'students' && (
                <div className="ov">
                  <ErrorAlert
                    error={apiErrors.students}
                    section="students"
                    onRetry={fetchAllData}
                  />

                  <section className="ov-panel ov-panel--flush">
                    <header className="ov-panel-head">
                      <h2 className="ov-panel-title">
                        Students
                        <span className="ov-panel-count">
                          {tabPagination.students.total}
                        </span>
                      </h2>
                      <button
                        type="button"
                        className="ov-panel-link"
                        onClick={() => navigate('/all-students')}
                      >
                        All students
                      </button>
                    </header>

                    <div className="td-list td-list--lms">
                      <NewCustomDataTable
                        data={filteredStudents}
                        headers={studentHeaders}
                        totalRecords={tabPagination.students.total}
                        currentPage={currentPages.students}
                        recordsPerPage={itemsPerPage}
                        onPageChange={handleStudentsPageChange}
                        loading={tabLoading}
                        loadingMore={loadingMoreStudentsTab}
                        serverSide={true}
                        showToolbar={false}
                        showPagination={false}
                        showFooter={false}
                        onRowClick={(row) =>
                          row.studentId && handleViewStudent(row.studentId)
                        }
                        validRowClickTagNames="*"
                      />
                    </div>

                    {/* Where the reader is, and the sentinel the observer
                        watches. */}
                    <div className="att-picklist-foot" ref={studentsTabEndRef}>
                      {loadingMoreStudentsTab ? (
                        <>
                          <span className="pg-spinner att-spinner" aria-hidden="true" />
                          <span>Loading more students...</span>
                        </>
                      ) : studentsHasMore ? (
                        <span>
                          {filteredStudents.length} of {tabPagination.students.total} students
                        </span>
                      ) : (
                        tabPagination.students.total > 0 && (
                          <span>
                            All {tabPagination.students.total} student
                            {tabPagination.students.total === 1 ? '' : 's'} loaded
                          </span>
                        )
                      )}
                    </div>
                  </section>
                </div>
              )}

              {activeTab === 'trainers' && (
                <div className="ov">
                  <ErrorAlert error={apiErrors.trainers} section="trainers" />

                  <section className="ov-panel ov-panel--flush">
                    <header className="ov-panel-head">
                      <h2 className="ov-panel-title">
                        Trainers
                        <span className="ov-panel-count">
                          {tabPagination.trainers.total}
                        </span>
                      </h2>
                      <button
                        type="button"
                        className="ov-panel-link"
                        onClick={() => navigate('/list-trainer')}
                      >
                        All trainers
                      </button>
                    </header>

                    <div className="td-list td-list--lms">
                      <NewCustomDataTable
                        data={filteredTrainers}
                        headers={trainerHeaders}
                        totalRecords={tabPagination.trainers.total}
                        currentPage={currentPages.trainers}
                        recordsPerPage={itemsPerPage}
                        onPageChange={handleTrainersPageChange}
                        loading={tabLoading}
                        loadingMore={loadingMoreTrainersTab}
                        serverSide={true}
                        showToolbar={false}
                        showPagination={false}
                        showFooter={false}
                        onRowClick={(row) =>
                          handleViewTrainer(row)
                        }
                        validRowClickTagNames="*"
                      />
                    </div>

                    <div className="att-picklist-foot" ref={trainersEndRef}>
                      {loadingMoreTrainersTab ? (
                        <>
                          <span className="pg-spinner att-spinner" aria-hidden="true" />
                          <span>Loading more trainers...</span>
                        </>
                      ) : trainersHasMore ? (
                        <span>
                          {filteredTrainers.length} of {tabPagination.trainers.total} trainers
                        </span>
                      ) : (
                        tabPagination.trainers.total > 0 && (
                          <span>
                            All {tabPagination.trainers.total} trainer
                            {tabPagination.trainers.total === 1 ? '' : 's'} loaded
                          </span>
                        )
                      )}
                    </div>
                  </section>
                </div>
              )}

              {activeTab === 'jobs' && (
                <div className="ov">
                  <ErrorAlert error={apiErrors.jobs} section="jobs" />

                  <section className="ov-panel ov-panel--flush">
                    <header className="ov-panel-head">
                      <h2 className="ov-panel-title">
                        Jobs
                        <span className="ov-panel-count">
                          {tabPagination.jobs.total}
                        </span>
                      </h2>
                      <button
                        type="button"
                        className="ov-panel-link"
                        onClick={() => navigate('/job-section')}
                      >
                        All jobs
                      </button>
                    </header>

                    <div className="td-list td-list--lms">
                      <NewCustomDataTable
                        data={filteredJobs}
                        headers={jobHeaders}
                        totalRecords={tabPagination.jobs.total}
                        currentPage={currentPages.jobs}
                        recordsPerPage={itemsPerPage}
                        onPageChange={handleJobsPageChange}
                        loading={tabLoading}
                        loadingMore={loadingMoreJobsTab}
                        serverSide={true}
                        showToolbar={false}
                        showPagination={false}
                        showFooter={false}
                        onRowClick={(row) => handleViewJob(row.id)}
                        validRowClickTagNames="*"
                      />
                    </div>

                    <div className="att-picklist-foot" ref={jobsEndRef}>
                      {loadingMoreJobsTab ? (
                        <>
                          <span className="pg-spinner att-spinner" aria-hidden="true" />
                          <span>Loading more jobs...</span>
                        </>
                      ) : jobsHasMore ? (
                        <span>
                          {filteredJobs.length} of {tabPagination.jobs.total} jobs
                        </span>
                      ) : (
                        tabPagination.jobs.total > 0 && (
                          <span>
                            All {tabPagination.jobs.total} job
                            {tabPagination.jobs.total === 1 ? '' : 's'} loaded
                          </span>
                        )
                      )}
                    </div>
                  </section>
                </div>
              )}

              {activeTab === 'events' && (
                <div className="ov">
                  <ErrorAlert error={apiErrors.events} section="events" />

                  <section className="ov-panel ov-panel--flush">
                    <header className="ov-panel-head">
                      <h2 className="ov-panel-title">
                        Events
                        <span className="ov-panel-count">
                          {tabPagination.events.total}
                        </span>
                      </h2>
                      <button
                        type="button"
                        className="ov-panel-link"
                        onClick={() => navigate('/list-event-form')}
                      >
                        All events
                      </button>
                    </header>

                    <div className="td-list td-list--lms">
                      <NewCustomDataTable
                        data={dashboardData.events}
                        headers={eventHeaders}
                        totalRecords={tabPagination.events.total}
                        currentPage={currentPages.events}
                        recordsPerPage={itemsPerPage}
                        onPageChange={handleEventsPageChange}
                        loading={tabLoading}
                        loadingMore={loadingMoreEventsTab}
                        serverSide={true}
                        showToolbar={false}
                        showPagination={false}
                        showFooter={false}
                        onRowClick={(row) => handleViewEvent(row)}
                        validRowClickTagNames="*"
                      />
                    </div>

                    <div className="att-picklist-foot" ref={eventsEndRef}>
                      {loadingMoreEventsTab ? (
                        <>
                          <span className="pg-spinner att-spinner" aria-hidden="true" />
                          <span>Loading more events...</span>
                        </>
                      ) : eventsHasMore ? (
                        <span>
                          {dashboardData.events.length} of {tabPagination.events.total} events
                        </span>
                      ) : (
                        tabPagination.events.total > 0 && (
                          <span>
                            All {tabPagination.events.total} event
                            {tabPagination.events.total === 1 ? '' : 's'} loaded
                          </span>
                        )
                      )}
                    </div>
                  </section>
                </div>
              )}

              {activeTab === 'access' && canReviewAccess && <AccessRequestsAdmin />}
            </div>
          </div>
        </div>
      {/* Full Job Description Modal */}
      {showFullJDModal && selectedJobForFullJD && (
        <div
          tabIndex="-1"
          role="dialog"
        >
          <div role="document">
            <div>
              <div>
                <h2>
                  Job Description - {selectedJobForFullJD.jobName}
                </h2>
                <button
                  type="button"
                  aria-label="Close"
                  onClick={() => {
                    setShowFullJDModal(false);
                    setSelectedJobForFullJD(null);
                  }}
                ></button>
              </div>
              <div>
                <div>
                  <h6>Full Job Description:</h6>
                  <div
                    dangerouslySetInnerHTML={safeHtml(
                      selectedJobForFullJD.jobDescription || 'No job description available',
                    )}
                  />
                </div>
              </div>
              <div>
                <button
                  type="button"
                  onClick={() => {
                    setShowFullJDModal(false);
                    setSelectedJobForFullJD(null);
                  }}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Batch Detail Modal */}
      {/* One student, in full. Rendered at the page root rather than
          inside the batch dialog: it is opened from the Students tab too,
          and a dialog nested in a closed dialog never mounts. */}
      <StudentDetailModal
        show={showStudentModal}
        onHide={() => setShowStudentModal(false)}
        studentData={selectedStudentData}
        fetchAttendance={getStudentAttendance}
        fetchQuizzes={getStudentQuizHistory}
      />

      {/* One event, in full. */}
      {eventDetails && (
        <div
          className="pg-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="ad-event-title"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setEventDetails(null);
          }}
        >
          <div className="pg-modal-panel pg-modal-panel--xl">
            <header className="pg-modal-head">
              <div className="pg-modal-heading">
                <span className="pg-modal-eyebrow">Event</span>
                <h3 className="pg-modal-title" id="ad-event-title">
                  {eventDetails.name || 'Untitled'}
                </h3>
                {eventDetails.category && (
                  <p className="pg-modal-sub">{eventDetails.category}</p>
                )}
              </div>
              <button
                type="button"
                className="pg-modal-close"
                onClick={() => setEventDetails(null)}
                aria-label="Close"
              >
                &times;
              </button>
            </header>

            <div className="lp-tabs" role="tablist" aria-label="Event">
              {[
                { key: 'details', label: 'Details' },
                {
                  key: 'trainers',
                  label: 'Trainers',
                  n: (eventDetails.trainerNames || []).length,
                },
                {
                  key: 'batches',
                  label: 'Batches',
                  n: (eventDetails.batchNames || []).length,
                },
              ].map((t) => (
                <button
                  type="button"
                  key={t.key}
                  role="tab"
                  id={`ad-ev-tab-${t.key}`}
                  aria-selected={eventTab === t.key}
                  aria-controls={`ad-ev-panel-${t.key}`}
                  className={`lp-tab${eventTab === t.key ? ' is-on' : ''}`}
                  onClick={() => setEventTab(t.key)}
                >
                  {t.label}
                  {t.n !== undefined && <span className="lp-tab-n">{t.n}</span>}
                </button>
              ))}
            </div>

            <div className="pg-modal-body">
              {eventTab === 'details' && (
                <div
                  role="tabpanel"
                  id="ad-ev-panel-details"
                  aria-labelledby="ad-ev-tab-details"
                >
                  <dl className="quiz-facts">
                    <div>
                      <dt>Date</dt>
                      <dd>{formatDate(eventDetails.date)}</dd>
                    </div>
                    <div>
                      <dt>Time</dt>
                      <dd>
                        {eventDetails.time
                          ? new Date(eventDetails.time).toLocaleTimeString(undefined, {
                              hour: '2-digit',
                              minute: '2-digit',
                            })
                          : 'All day'}
                      </dd>
                    </div>
                    <div>
                      <dt>Classroom</dt>
                      <dd>{eventDetails.classroomNumber || '—'}</dd>
                    </div>
                    <div>
                      <dt>Address</dt>
                      <dd>{eventDetails.offlineAddress || '—'}</dd>
                    </div>
                    <div>
                      <dt>Organiser</dt>
                      <dd>{eventDetails.organizerName || '—'}</dd>
                    </div>
                    <div>
                      <dt>Organiser phone</dt>
                      <dd>{eventDetails.organizerPhone || '—'}</dd>
                    </div>
                  </dl>

                  {eventDetails.onlineLink && eventDetails.onlineLink !== 'N/A' && (
                    <>
                      <h5 className="pgm-section-title">Meeting</h5>
                      <p className="ov-meeting">
                        <a
                          href={eventDetails.onlineLink}
                          target="_blank"
                          rel="noreferrer noopener"
                        >
                          Join online
                        </a>
                      </p>
                    </>
                  )}

                  {eventDetails.description && (
                    <>
                      <h5 className="pgm-section-title">Description</h5>
                      <p className="lp-prose">{eventDetails.description}</p>
                    </>
                  )}
                </div>
              )}

              {eventTab === 'trainers' && (
                <div
                  role="tabpanel"
                  id="ad-ev-panel-trainers"
                  aria-labelledby="ad-ev-tab-trainers"
                >
                  {(eventDetails.trainerNames || []).length === 0 ? (
                    <p className="lp-muted">No trainer is on this event.</p>
                  ) : (
                    <div className="lp-dialog-table">
                      <table>
                        <thead>
                          <tr>
                            <th>Sr. No</th>
                            <th>Trainer</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(eventDetails.trainerNames || []).map((name, i) => (
                            <tr key={`${name}-${i}`}>
                              <td>{i + 1}</td>
                              <td>{name}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}

              {eventTab === 'batches' && (
                <div
                  role="tabpanel"
                  id="ad-ev-panel-batches"
                  aria-labelledby="ad-ev-tab-batches"
                >
                  {(eventDetails.batchNames || []).length === 0 ? (
                    <p className="lp-muted">No batch is on this event.</p>
                  ) : (
                    <div className="lp-dialog-table">
                      <table>
                        <thead>
                          <tr>
                            <th>Sr. No</th>
                            <th>Batch</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(eventDetails.batchNames || []).map((name, i) => (
                            <tr key={`${name}-${i}`}>
                              <td>{i + 1}</td>
                              <td>{name}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}
            </div>

            <footer className="pg-modal-foot">
              <div className="pgm-actions">
                <button
                  type="button"
                  className="pgm-btn"
                  onClick={() => navigate('/list-event-form')}
                >
                  Open in events
                </button>
              </div>
              <button
                type="button"
                className="pg-btn pg-btn--main"
                onClick={() => setEventDetails(null)}
              >
                Close
              </button>
            </footer>
          </div>
        </div>
      )}

      {/* One job, in full. */}
      {jobDetails && (
        <div
          className="pg-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="ad-job-title"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setJobDetails(null);
          }}
        >
          <div className="pg-modal-panel pg-modal-panel--wide">
            <header className="pg-modal-head">
              <div className="pg-modal-heading">
                <span className="pg-modal-eyebrow">Job</span>
                <h3 className="pg-modal-title" id="ad-job-title">
                  {jobDetails.jobName || 'Untitled'}
                </h3>
                {(jobDetails.organizationName || jobDetails.company) && (
                  <p className="pg-modal-sub">
                    {jobDetails.organizationName || jobDetails.company}
                  </p>
                )}
              </div>
              <button
                type="button"
                className="pg-modal-close"
                onClick={() => setJobDetails(null)}
                aria-label="Close"
              >
                &times;
              </button>
            </header>

            <div className="pg-modal-body">
              <dl className="quiz-facts">
                <div>
                  <dt>Status</dt>
                  <dd>
                    <span
                      className={`pg-pill ${
                        String(jobDetails.status || '').toLowerCase() === 'active'
                          ? 'pg-pill--good'
                          : ''
                      }`.trim()}
                    >
                      {jobDetails.status || 'Unknown'}
                    </span>
                  </dd>
                </div>
                <div>
                  <dt>Location</dt>
                  <dd>{jobDetails.jobLocation || '—'}</dd>
                </div>
                <div>
                  <dt>Salary</dt>
                  <dd>{jobDetails.salary || 'Confidential'}</dd>
                </div>
                <div>
                  <dt>Posted</dt>
                  <dd>
                    {jobDetails.createdAt
                      ? new Date(jobDetails.createdAt).toLocaleDateString()
                      : '—'}
                  </dd>
                </div>
              </dl>

              {jobDetails.jobDescription && (
                <>
                  <h5 className="pgm-section-title">Description</h5>
                  <p className="lp-prose">{jobDetails.jobDescription}</p>
                </>
              )}
            </div>

            <footer className="pg-modal-foot">
              <div className="pgm-actions">
                <button
                  type="button"
                  className="pgm-btn"
                  onClick={() => navigate('/job-section')}
                >
                  Open in jobs
                </button>
              </div>
              <button
                type="button"
                className="pg-btn pg-btn--main"
                onClick={() => setJobDetails(null)}
              >
                Close
              </button>
            </footer>
          </div>
        </div>
      )}

      {/* One trainer, in full. The SweetAlert sheet this replaces was an
          HTML string with no stylesheet behind it. */}
      {trainerDetails && (
        <div
          className="pg-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="ad-trainer-title"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setTrainerDetails(null);
          }}
        >
          <div className="pg-modal-panel pg-modal-panel--wide">
            <header className="pg-modal-head">
              <div className="pg-modal-heading">
                <span className="pg-modal-eyebrow">Trainer</span>
                <h3 className="pg-modal-title" id="ad-trainer-title">
                  {trainerDetails.trainerName || trainerDetails.name || 'Unnamed'}
                </h3>
                {(trainerDetails.trainerEmail || trainerDetails.email) && (
                  <p className="pg-modal-sub">
                    {trainerDetails.trainerEmail || trainerDetails.email}
                  </p>
                )}
              </div>
              <button
                type="button"
                className="pg-modal-close"
                onClick={() => setTrainerDetails(null)}
                aria-label="Close"
              >
                &times;
              </button>
            </header>

            <div className="pg-modal-body">
              <dl className="quiz-facts">
                <div>
                  <dt>Specialization</dt>
                  <dd>
                    {trainerDetails.specialization ||
                      trainerDetails.expertise ||
                      '—'}
                  </dd>
                </div>
                <div>
                  <dt>Experience</dt>
                  <dd>
                    {trainerDetails.experience
                      ? `${trainerDetails.experience} years`
                      : '—'}
                  </dd>
                </div>
                <div>
                  <dt>Phone</dt>
                  {/* From the trainer's login account (undefined while it loads). */}
                  <dd>
                    {trainerDetails.contact === undefined
                      ? 'Loading…'
                      : trainerDetails.contact?.mobileNumber || '—'}
                  </dd>
                </div>
                <div>
                  <dt>Added</dt>
                  <dd>
                    {trainerDetails.createdAt
                      ? new Date(trainerDetails.createdAt).toLocaleDateString()
                      : '—'}
                  </dd>
                </div>
              </dl>
            </div>

            <footer className="pg-modal-foot">
              <div className="pgm-actions">
                <button
                  type="button"
                  className="pgm-btn"
                  onClick={() => navigate('/list-trainer')}
                >
                  Open in trainers
                </button>
              </div>
              <button
                type="button"
                className="pg-btn pg-btn--main"
                onClick={() => setTrainerDetails(null)}
              >
                Close
              </button>
            </footer>
          </div>
        </div>
      )}

      {/* One course, in full. The SweetAlert sheet this replaces was a
          string of HTML with no stylesheet behind it. */}
      {courseDetails && (
        <div
          className="pg-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="ad-course-title"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setCourseDetails(null);
          }}
        >
          <div className="pg-modal-panel pg-modal-panel--wide">
            <header className="pg-modal-head">
              <div className="pg-modal-heading">
                <span className="pg-modal-eyebrow">Course</span>
                <h3 className="pg-modal-title" id="ad-course-title">
                  {courseDetails.name}
                </h3>
                {courseDetails.code && (
                  <p className="pg-modal-sub">{courseDetails.code}</p>
                )}
              </div>
              <button
                type="button"
                className="pg-modal-close"
                onClick={() => setCourseDetails(null)}
                aria-label="Close"
              >
                &times;
              </button>
            </header>

            <div className="pg-modal-body">
              <dl className="quiz-facts">
                <div>
                  <dt>Duration</dt>
                  <dd>{courseDetails.duration || '—'}</dd>
                </div>
                <div>
                  <dt>Branch</dt>
                  <dd>{courseDetails.branchOfCourse || '—'}</dd>
                </div>
                <div>
                  <dt>Department</dt>
                  <dd>{courseDetails.departmentOfCourse || '—'}</dd>
                </div>
                <div>
                  <dt>Syllabus topics</dt>
                  <dd>{courseDetails.syllabusTopicCount || 0}</dd>
                </div>
              </dl>

              {courseDetails.syllabusTopicPreview && (
                <>
                  <h5 className="pgm-section-title">Last topic</h5>
                  <p className="lp-prose">{courseDetails.syllabusTopicPreview}</p>
                </>
              )}

              {courseDetails.description && (
                <>
                  <h5 className="pgm-section-title">Description</h5>
                  <p className="lp-prose">{courseDetails.description}</p>
                </>
              )}
            </div>

            <footer className="pg-modal-foot">
              <div className="pgm-actions">
                <button
                  type="button"
                  className="pgm-btn"
                  onClick={() => navigate('/list-course')}
                >
                  Open in courses
                </button>
              </div>
              <button
                type="button"
                className="pg-btn pg-btn--main"
                onClick={() => setCourseDetails(null)}
              >
                Close
              </button>
            </footer>
          </div>
        </div>
      )}

      {showBatchModal && (
        <div
          className="pg-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="ad-batch-title"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setShowBatchModal(false);
          }}
        >
          <div className="pg-modal-panel pg-modal-panel--xl">
        <header className="pg-modal-head">
          <div className="pg-modal-heading">
            <span className="pg-modal-eyebrow">Batch</span>
            <h3 className="pg-modal-title" id="ad-batch-title">
              {selectedBatch?.name || 'Batch details'}
            </h3>
            <p className="pg-modal-sub">
              Everything on this batch, and who is enrolled.
            </p>
          </div>
          <button
            type="button"
            className="pg-modal-close"
            onClick={() => setShowBatchModal(false)}
            aria-label="Close"
          >
            &times;
          </button>
        </header>

        <div className="lp-tabs" role="tablist" aria-label="Batch">
          {[
            { key: 'details', label: 'Details' },
            {
              key: 'students',
              label: 'Students',
              n: selectedBatch?.students?.length || 0,
            },
          ].map((t) => (
            <button
              type="button"
              key={t.key}
              role="tab"
              id={`ad-batch-tab-${t.key}`}
              aria-selected={batchModalTab === t.key}
              aria-controls={`ad-batch-panel-${t.key}`}
              className={`lp-tab${batchModalTab === t.key ? ' is-on' : ''}`}
              onClick={() => setBatchModalTab(t.key)}
            >
              {t.label}
              {t.n !== undefined && <span className="lp-tab-n">{t.n}</span>}
            </button>
          ))}
        </div>

        <div className="pg-modal-body">
          {selectedBatch && batchModalTab === 'details' && (
            <div
              role="tabpanel"
              id="ad-batch-panel-details"
              aria-labelledby="ad-batch-tab-details"
            >
              <dl className="quiz-facts">
                <div>
                  <dt>Course</dt>
                  <dd>{selectedBatch.courseDetails?.name || '—'}</dd>
                </div>
                <div>
                  <dt>Course code</dt>
                  <dd>{selectedBatch.courseDetails?.code || '—'}</dd>
                </div>
                <div>
                  <dt>Duration</dt>
                  <dd>{selectedBatch.courseDetails?.duration || '—'}</dd>
                </div>
                <div>
                  <dt>Trainer</dt>
                  <dd>{selectedBatch.trainerDetails?.trainerName || 'Not assigned'}</dd>
                </div>
                <div>
                  <dt>Branch</dt>
                  <dd>{selectedBatch.courseDetails?.branchOfCourse || '—'}</dd>
                </div>
                <div>
                  <dt>Mode</dt>
                  <dd>{selectedBatch.mode || '—'}</dd>
                </div>
                <div>
                  <dt>Starts</dt>
                  <dd>{formatDate(selectedBatch.startTime || selectedBatch.startDate)}</dd>
                </div>
                <div>
                  <dt>Ends</dt>
                  <dd>{formatDate(selectedBatch.endTime || selectedBatch.endDate)}</dd>
                </div>
              </dl>

              {selectedBatch.meetingLink && (
                <>
                  <h5 className="pgm-section-title">Meeting</h5>
                  <p className="ov-meeting">
                    <a
                      href={selectedBatch.meetingLink}
                      target="_blank"
                      rel="noreferrer noopener"
                    >
                      Join the session
                    </a>
                    {selectedBatch.meetingId && (
                      <span className="ov-meeting-id">
                        ID {selectedBatch.meetingId}
                      </span>
                    )}
                  </p>
                </>
              )}

            </div>
          )}

          {selectedBatch && batchModalTab === 'students' && (
            <div
              role="tabpanel"
              id="ad-batch-panel-students"
              aria-labelledby="ad-batch-tab-students"
            >
              {selectedBatch.students && selectedBatch.students.length > 0 ? (
                <div className="lp-dialog-table">
                  <table>
                    <thead>
                      <tr>
                        <th>Sr. No</th>
                        <th>Student</th>
                        <th>Email</th>
                        <th>Phone</th>
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      {selectedBatch.students.map((student, index) => {
                        const sid = student.id || student.studentId;
                        return (
                          <tr key={sid || index}>
                            <td>{index + 1}</td>
                            <td>{student.name || student.studentName || 'Unnamed'}</td>
                            <td>{student.email || student.studentEmail || '—'}</td>
                            <td>{student.phone || student.studentPhoneNumber || '—'}</td>
                            <td className="wp-rowact">
                              {/* The roster was a dead end: it named eighteen
                                  people and gave no way to reach any of them.
                                  This modal closes first so the two are not
                                  stacked, and Waypoint's own back link is
                                  what returns the reader. */}
                              {sid && (
                                <button
                                  type="button"
                                  className="wp-link"
                                  onClick={() => {
                                    setShowBatchModal(false);
                                    openEntity('student', sid);
                                  }}
                                >
                                  Open
                                </button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="lp-muted">No students are enrolled in this batch.</p>
              )}
            </div>
          )}
        </div>

        <footer className="pg-modal-foot">
          <button
            type="button"
            className="pg-btn pg-btn--main"
            onClick={() => setShowBatchModal(false)}
          >
            Close
          </button>
        </footer>
      </div>
        </div>
      )}
    </div>
  );
};

export default memo(AdminDashboard);
